import ChevronRight from '@material-ui/icons/ChevronRight';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Form, FormRenderProps, FormSpy } from 'react-final-form';
import { CharacterMaskRegex } from '~domain/helper/validation-regex';
import { TextFormField } from '~frontend-common/components/form-fields';
import { FormFields, SentryErrorBox } from '~frontend-common/components/ui';
import { useCountdown } from '~frontend-common/hooks/use-countdown';
import { useTranslator } from '~frontend-common/i18n/translator';
import { Box } from '~ui-kit/components/box/box';
import { Button } from '~ui-kit/components/button/button';
import { Typography } from '~ui-kit/components/typography/typography';
import { authService } from '~web-portal/services';
import { PageTitle } from './page-title';

interface OneTimePasscodeFormProps {
  email: string;
  passcodeToken: string;
  onRequestNewPasscode: () => void;
  onSuccess: () => void;
}

interface OneTimePasscodeFormValues {
  passcode?: string;
}

const PASSCODE_MASK = [
  CharacterMaskRegex.DIGIT_REGEX,
  CharacterMaskRegex.DIGIT_REGEX,
  CharacterMaskRegex.DIGIT_REGEX,
  CharacterMaskRegex.DIGIT_REGEX,
  CharacterMaskRegex.DIGIT_REGEX,
  CharacterMaskRegex.DIGIT_REGEX,
];

export function OneTimePasscodeForm({
  email,
  passcodeToken,
  onRequestNewPasscode,
  onSuccess,
}: OneTimePasscodeFormProps) {
  const translator = useTranslator();
  const { tl } = translator;
  const timer = useCountdown(authService.OTP_REQUEST_COOLDOWN_SECONDS, true);

  const loginMutation = useMutation({
    mutationFn: ({ token, passcode }: { token: string; passcode: string }) => {
      return authService.login(token, passcode);
    },
    onSuccess: () => onSuccess(),
  });

  const handleNewPasscodeRequest = () => {
    onRequestNewPasscode();

    timer.start();
  };

  const handleFormValuesChange = (values: OneTimePasscodeFormValues) => {
    const { passcode } = values;

    if (passcode?.length === 6) {
      loginMutation.mutate({ token: passcodeToken, passcode });
    }
  };

  const isResendInCooldown = timer.isRunning && timer.timeLeft > 0;

  return (
    <Form<OneTimePasscodeFormValues>
      onSubmit={handleNewPasscodeRequest}
      render={({ handleSubmit }: FormRenderProps) => {
        return (
          <FormFields onSubmit={handleSubmit}>
            <FormSpy
              subscription={{ values: true }}
              onChange={(formState) => handleFormValuesChange(formState.values)}
            />

            <PageTitle $mb={3}>{tl('auth.passcodeForm.title')}</PageTitle>

            <Typography $textAlign="center" component="p" $mb={4}>
              {tl('auth.passcodeForm.desc', { fillers: [email] })}
            </Typography>

            {loginMutation.isError && (
              <SentryErrorBox
                error={loginMutation.error}
                messageOverride={tl('auth.passcodeForm.error')}
                translator={translator}
                mb={3}
              />
            )}

            <TextFormField
              name="passcode"
              inputMaskConfig={{
                mask: PASSCODE_MASK,
              }}
              label={tl('auth.passcodeForm.label')}
              autoFocus
              required
              disabled={loginMutation.isLoading}
            />

            <Box justifyContent="center" mt={5}>
              <Button
                data-id="btn-resend-passcode"
                type="submit"
                appearance="none"
                color="primary"
                size="md"
                isLoading={loginMutation.isLoading}
                disabled={loginMutation.isLoading || isResendInCooldown}
                $px={3}
              >
                <Typography
                  $fontSize="14px"
                  $fontWeight="normal"
                  muiProps={{ style: { textDecoration: 'underline', textTransform: 'none' } }}
                  noWrap
                >
                  {isResendInCooldown
                    ? tl('auth.passcodeForm.resendButton.cooldownLabel', { fillers: [timer.timeLeft] })
                    : tl('auth.passcodeForm.resendButton.readyLabel')}
                </Typography>

                <ChevronRight />
              </Button>
            </Box>
          </FormFields>
        );
      }}
    />
  );
}
